import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  History as HistoryIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { cpeService } from '../services/cpeService';
import { CPE, CPEAuditLog } from '../types/cpe';

export const CPEDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [cpe, setCPE] = useState<CPE | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [auditLogs, setAuditLogs] = useState<CPEAuditLog[]>([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [loadingAuditLog, setLoadingAuditLog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const canDelete = isSuperAdmin || user?.can_delete_cpe;

  const loadCPE = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const data = await cpeService.getCPEById(id);
      setCPE(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to load CPE');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadCPE();
    }
  }, [id, loadCPE]);

  const loadAuditLog = async () => {
    try {
      setLoadingAuditLog(true);
      const response = await cpeService.getCPEAuditLog(id!);
      setAuditLogs(response.data.auditLogs);
      setShowAuditLog(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to load audit log');
    } finally {
      setLoadingAuditLog(false);
    }
  };

  const handleDelete = async () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await cpeService.deleteCPE(id!);
      setDeleteDialogOpen(false);
      navigate('/cpes');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to delete CPE');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async () => {
    setRestoreDialogOpen(true);
  };

  const handleRestoreConfirm = async () => {
    try {
      setRestoring(true);
      await cpeService.restoreCPE(id!);
      setRestoreDialogOpen(false);
      loadCPE();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to restore CPE');
      setRestoreDialogOpen(false);
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!cpe) {
    return (
      <Container>
        <Alert severity="error">CPE not found</Alert>
      </Container>
    );
  }

  const InfoRow = ({ label, value }: { label: string; value: string | number | null | undefined | React.ReactNode }) => (
    <Grid container spacing={2} sx={{ py: 1 }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography variant="body1">{value || '-'}</Typography>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {cpe.name}
            </Typography>
            {cpe.deleted_at && (
              <Chip label="DELETED" color="error" sx={{ mb: 2 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(-1)}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={loadAuditLog}
              disabled={loadingAuditLog}
            >
              {t('cpe.history')}
            </Button>
            {isAdmin && !cpe.deleted_at && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/cpes/${id}/edit`)}
              >
                {t('common.edit')}
              </Button>
            )}
            {isAdmin && canDelete && !cpe.deleted_at && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                {t('common.delete')}
              </Button>
            )}
            {isSuperAdmin && cpe.deleted_at && (
              <Button
                variant="contained"
                color="success"
                startIcon={<RestoreIcon />}
                onClick={handleRestore}
              >
                {t('cpe.restoreCpe')}
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Image */}
          {cpe.image_url && (
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Avatar
                  src={cpeService.getImageUrl(cpe.image_url) || undefined}
                  alt={cpe.name}
                  variant="rounded"
                  sx={{ width: '100%', height: 'auto', maxHeight: 400 }}
                />
              </Paper>
            </Grid>
          )}

          {/* Details */}
          <Grid item xs={12} md={cpe.image_url ? 8 : 12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('cpe.basicInformation')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <InfoRow label={t('cpe.fields.name')} value={cpe.name} />
              <InfoRow label={t('cpe.fields.companyCui')} value={cpe.company_cui} />
              <InfoRow
                label={t('cpe.fields.plantType')}
                value={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {cpe.plant_type.map((type) => (
                      <Chip key={type} label={type} size="small" />
                    ))}
                  </Box>
                }
              />
              <InfoRow
                label={t('cpe.fields.location')}
                value={
                  <Typography
                    component="span"
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                    onClick={() => navigate('/', {
                      state: {
                        centerLat: Number(cpe.latitude),
                        centerLng: Number(cpe.longitude),
                        zoom: 10,
                        cpeId: cpe.id
                      }
                    })}
                    title="View on map"
                  >
                    {`${Number(cpe.latitude).toFixed(6)}, ${Number(cpe.longitude).toFixed(6)}`}
                  </Typography>
                }
              />
              <InfoRow
                label={t('cpe.fields.locality', 'Locality, County')}
                value={[cpe.locality, cpe.county].filter(Boolean).join(', ')}
              />
              <InfoRow label={t('cpe.fields.power')} value={cpe.power?.toLocaleString()} />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                {t('cpe.status')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <InfoRow
                label={t('cpe.fields.cpeStatus')}
                value={cpe.cpe_status ? <Chip label={cpe.cpe_status} /> : '-'}
              />
              <InfoRow
                label={t('cpe.fields.commercialStatus')}
                value={cpe.commercial_status ? <Chip label={cpe.commercial_status} variant="outlined" /> : '-'}
              />
              <InfoRow
                label={t('cpe.fields.internalPositioning')}
                value={
                  cpe.internal_positioning ? (
                    <Chip
                      label={cpe.internal_positioning === 'go' ? t('cpe.positioning.go') : t('cpe.positioning.noGo')}
                      color={cpe.internal_positioning === 'go' ? 'success' : 'error'}
                    />
                  ) : '-'
                }
              />
              <InfoRow label={t('cpe.fields.energizationYear')} value={cpe.energization_year} />
              <InfoRow
                label={t('cpe.fields.zone')}
                value={
                  cpe.zone_id ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => navigate(`/zones/${cpe.zone_id}`)}
                      sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                    >
                      {cpe.zone?.name || cpe.zone_id}
                    </Button>
                  ) : '-'
                }
              />
              {/* Next Action */}
              <InfoRow
                label={t('nextAction.label', 'Next Action')}
                value={
                  cpe.next_action_id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={cpe.nextAction?.name || cpe.next_action_id}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                      {cpe.next_action_due_date && (() => {
                        const today = new Date().toISOString().split('T')[0];
                        const isOverdue = cpe.next_action_due_date < today;
                        return (
                          <Chip
                            label={
                              isOverdue
                                ? `${t('nextAction.overdue', 'Overdue')} — ${cpe.next_action_due_date}`
                                : `${t('nextAction.dueOn', 'Due {{date}}', { date: cpe.next_action_due_date })}`
                            }
                            size="small"
                            color={isOverdue ? 'error' : 'default'}
                            variant={isOverdue ? 'filled' : 'outlined'}
                          />
                        );
                      })()}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('nextAction.noAction', 'No action set')}
                    </Typography>
                  )
                }
              />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                {t('cpe.additionalDetails')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <InfoRow label={t('cpe.fields.epc')} value={cpe.epc} />
              <InfoRow label={t('cpe.fields.dispatcher')} value={cpe.dispatcher} />
              <InfoRow label={t('cpe.fields.maintenanceCompany')} value={cpe.maintenance_company} />
              <InfoRow label={t('cpe.fields.omAccountManager')} value={cpe.accountManager?.name || cpe.om_account_manager} />
              <InfoRow
                label={t('cpe.fields.exactLocation', 'Exact Location')}
                value={
                  <Chip
                    label={cpe.exact_location ? t('common.yes') : t('common.no')}
                    color={cpe.exact_location ? 'success' : 'warning'}
                    size="small"
                  />
                }
              />
              <InfoRow
                label={t('cpe.fields.isWaldevarPP', 'Waldevar PP')}
                value={
                  <Chip
                    label={cpe.is_waldevar_pp ? t('common.yes') : t('common.no')}
                    color={cpe.is_waldevar_pp ? 'primary' : 'default'}
                    size="small"
                    sx={cpe.is_waldevar_pp ? { bgcolor: '#1d388d', color: '#fff' } : {}}
                  />
                }
              />
              <InfoRow
                label={t('cpe.fields.internalLink', 'Internal Link')}
                value={
                  cpe.internal_link ? (
                    <a
                      href={cpe.internal_link.startsWith('http') ? cpe.internal_link : `https://${cpe.internal_link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {cpe.internal_link}
                    </a>
                  ) : '-'
                }
              />
              <InfoRow
                label={t('cpe.fields.externalLink', 'External Link')}
                value={
                  cpe.project_link ? (
                    <a
                      href={cpe.project_link.startsWith('http') ? cpe.project_link : `https://${cpe.project_link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {cpe.project_link}
                    </a>
                  ) : '-'
                }
              />
              <InfoRow label={t('cpe.fields.shortDescription')} value={cpe.short_description} />
              <InfoRow label={t('cpe.fields.notes')} value={cpe.notes} />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                {t('cpe.metadata')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <InfoRow
                label={t('cpe.fields.createdBy')}
                value={
                  cpe.creator
                    ? `${cpe.creator.name} (${cpe.creator.email})`
                    : '-'
                }
              />
              <InfoRow
                label={t('cpe.fields.createdAt')}
                value={new Date(cpe.created_at).toLocaleString()}
              />
              <InfoRow
                label={t('cpe.fields.lastUpdatedBy')}
                value={
                  cpe.updater
                    ? `${cpe.updater.name} (${cpe.updater.email})`
                    : '-'
                }
              />
              <InfoRow
                label={t('cpe.fields.lastUpdatedAt')}
                value={new Date(cpe.updated_at).toLocaleString()}
              />
              {cpe.deleted_at && (
                <InfoRow
                  label="Deleted At"
                  value={new Date(cpe.deleted_at).toLocaleString()}
                />
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Audit Log Dialog */}
        <Dialog
          open={showAuditLog}
          onClose={() => setShowAuditLog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{t('cpe.auditLog')}</DialogTitle>
          <DialogContent>
            {auditLogs.length === 0 ? (
              <Typography>{t('common.noHistory', 'No history available')}</Typography>
            ) : (
              <List>
                {auditLogs.map((log) => (
                  <ListItem key={log.id} divider>
                    <ListItemText
                      primary={
                        <Box>
                          <Chip label={log.action.toUpperCase()} size="small" sx={{ mr: 1 }} />
                          <Typography component="span">
                            by {log.user?.email || 'Unknown'}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" display="block">
                            {new Date(log.created_at).toLocaleString()}
                          </Typography>
                          {log.changed_fields && (
                            <Typography variant="caption" display="block">
                              Changed fields: {Object.keys(log.changed_fields).join(', ')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAuditLog(false)}>{t('common.close')}</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !deleting && setDeleteDialogOpen(false)}
        >
          <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('common.deleteConfirmMessage')} <strong>{cpe?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? t('common.loading') : t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Restore Confirmation Dialog */}
        <Dialog
          open={restoreDialogOpen}
          onClose={() => !restoring && setRestoreDialogOpen(false)}
        >
          <DialogTitle>{t('cpe.restoreCpe')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('cpe.confirmRestore')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleRestoreConfirm}
              color="success"
              variant="contained"
              disabled={restoring}
            >
              {restoring ? t('common.loading') : t('cpe.restoreCpe')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
